import React, { useEffect, useState } from 'react';
import CustomModal from '../../../components/customModal/CustomModal';
import { Button, Form, Table } from 'react-bootstrap';
import {
    EcwManufacturerRecord,
    ECW_THINJ_MANUFACTURER_NOT_AVAILABLE,
    ECW_THINJ_MANUFACTURER_FIX_HINT,
    ECW_THINJ_MANUFACTURER_MULTIPLE_WARNING_MSG,
    getEcwThinjManufacturerSingleWarningMsg,
} from '../EcwConstants';

interface EcwManufacturerWarningDialogProps {
    show: boolean;
    scannedManufacturer: string;
    manufacturers: EcwManufacturerRecord[];
    selectedManufacturer?: EcwManufacturerRecord | null;
    onAccept: (selectedMfg: EcwManufacturerRecord) => void;
    onCancel: () => void;
}

const isMatchManufacturer = (
    a: EcwManufacturerRecord | null | undefined,
    b: EcwManufacturerRecord
): boolean => {
    if (!a) return false;
    if (a.manufacturerId && b.manufacturerId) {
        return a.manufacturerId === b.manufacturerId;
    }
    return a.name === b.name && a.manufacturerCode === b.manufacturerCode;
};

const EcwManufacturerWarningDialog: React.FC<EcwManufacturerWarningDialogProps> = ({
    show,
    scannedManufacturer,
    manufacturers,
    selectedManufacturer,
    onAccept,
    onCancel,
}) => {
    const [selectedOption, setSelectedOption] = useState<EcwManufacturerRecord | null>(null);

    useEffect(() => {
        if (show) {
            if (manufacturers.length === 1) {
                setSelectedOption(manufacturers[0]);
            } else if (selectedManufacturer) {
                const matched = manufacturers.find(m => isMatchManufacturer(selectedManufacturer, m));
                setSelectedOption(matched ?? null);
            } else {
                setSelectedOption(null);
            }
        }
    }, [show, manufacturers, selectedManufacturer]);

    if (!show) return null;

    const isZero = manufacturers.length === 0;
    const isSingle = manufacturers.length === 1;
    const isMultiple = manufacturers.length > 1;

    const handleAccept = () => {
        const target = selectedOption || (isSingle ? manufacturers[0] : null);
        if (target) {
            onAccept(target);
        }
    };

    const ecw_manufacturer = manufacturers[0]?.name || '';

    const warningMessage = isZero
        ? ECW_THINJ_MANUFACTURER_NOT_AVAILABLE
        : isSingle
            ? getEcwThinjManufacturerSingleWarningMsg(ecw_manufacturer)
            : ECW_THINJ_MANUFACTURER_MULTIPLE_WARNING_MSG;

    return (
        <CustomModal
            id="ManufacturerWarningModal"
            title="Warning"
            overlayClass="ecw-manufacturer-modal"
            content={
                <div>
                    <div className="alert alert-info ecw-manufacturer-modal__hint-banner mb-3" role="alert">
                        <i className="bi bi-info-circle-fill" />
                        <div>{ECW_THINJ_MANUFACTURER_FIX_HINT}</div>
                    </div>

                    <p className="warning-message" dangerouslySetInnerHTML={{ __html: warningMessage }} />

                    {isZero && (
                        <ul>
                            <li>
                                Scanned : <strong className="text-secondary">{scannedManufacturer || 'N/A'}</strong>
                            </li>
                            <li>
                                eCW : <strong className="text-danger">Not available</strong>
                            </li>
                        </ul>
                    )}

                    {isSingle && (
                        <ul>
                            <li>
                                Scanned : <strong className="text-secondary">{scannedManufacturer || 'N/A'}</strong>
                            </li>
                            <li>
                                eCW : <strong className="text-primary">{manufacturers[0]?.name} ({manufacturers[0]?.manufacturerCode})</strong>
                            </li>
                        </ul>
                    )}

                    {isMultiple && (
                        <div>
                            <ul className="mb-2">
                                <li>
                                    Scanned : <strong className="text-secondary">{scannedManufacturer || 'N/A'}</strong>
                                </li>
                            </ul>
                            <div className="ecw-manufacturer-modal__table-wrapper">
                                <Table bordered={false} hover className="m-0">
                                    <thead>
                                        <tr>
                                            <th className="text-center" style={{ width: '60px' }}>Select</th>
                                            <th>eCW Manufacturer</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {manufacturers.map((mfg, index) => {
                                            const isSelected = isMatchManufacturer(selectedOption, mfg);
                                            const uniqueKey = mfg.manufacturerId
                                                ? mfg.manufacturerId
                                                : `${mfg.manufacturerCode || 'mfg'}-${mfg.name}-${index}`;
                                            const radioId = `mfg-radio-${index}`;

                                            return (
                                                <tr
                                                    key={uniqueKey}
                                                    className={isSelected ? 'selected selected-row' : ''}
                                                    onClick={() => setSelectedOption(mfg)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <td className="text-center">
                                                        <Form.Check
                                                            type="radio"
                                                            id={radioId}
                                                            name="ecwManufacturerOption"
                                                            checked={isSelected}
                                                            onChange={() => setSelectedOption(mfg)}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setSelectedOption(mfg);
                                                            }}
                                                        />
                                                    </td>
                                                    <td className="text-left">
                                                        {mfg.name} <strong>({mfg.manufacturerCode})</strong>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </Table>
                            </div>
                        </div>
                    )}

                    <div className="d-flex gap-2 justify-content-end mt-4">
                        <Button variant="outline-primary" onClick={onCancel}>
                            Cancel
                        </Button>
                        {!isZero && (
                            <Button
                                variant="primary"
                                disabled={isSingle ? false : !selectedOption}
                                onClick={handleAccept}
                            >
                                Continue
                            </Button>
                        )}
                    </div>
                </div >
            }
        />
    );
};

export default EcwManufacturerWarningDialog;
